import { ActorReference } from '@/features/references/ActorReference';
// src/features/task/TaskAssigneeControl.tsx
// Assignee control for the task detail rail: assign, reassign, or clear the
// assignee. Wraps AssignTask / UnassignTask.

import { useState } from 'react';
import { observer } from '@react';
import { useTask, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardContent,
  Alert,
} from '@react/components';
import { UserPicker, type UserPickerScope } from '@/components/UserPicker';
import { errMsg } from '@/features/task/panelHelpers';

interface TaskAssigneeControlProps {
  taskId: string;
  peopleScope?: UserPickerScope;
  assignee?: any;
  editable?: boolean;
  onChanged?: () => void;
  /**
   * Render the control alone, without the card and the heading. Used where the
   * label already exists — inside the properties panel, which owns the labels
   * for every row and would otherwise print this one twice.
   */
  bare?: boolean;
}

export const TaskAssigneeControl = observer(({
  taskId,
  peopleScope,
  bare,
  assignee,
  editable = true,
  onChanged,
}: TaskAssigneeControlProps) => {
  const store = useTask();
  const { t } = useI18n();

  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assign = async (userId: string, displayName: string) => {
    const id = userId.trim();
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      await store.AssignTask(taskId, {
        assignee: { entity: { id }, display_name: displayName || undefined },
      } as any);
      setFormOpen(false);
      onChanged?.();
    } catch (e) {
      setError(errMsg(e, t('task.assignControl.assignError')));
    } finally {
      setBusy(false);
    }
  };

  const unassign = async () => {
    setBusy(true);
    setError(null);
    try {
      await store.UnassignTask(taskId);
      onChanged?.();
    } catch (e) {
      setError(errMsg(e, t('task.assignControl.unassignError')));
    } finally {
      setBusy(false);
    }
  };

  const body = (
    <>
        {error && <Alert variant="error">{error}</Alert>}

        {assignee ? (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <ActorReference actor={assignee} className="text-sm font-medium" />
            </div>
            {editable && (
              <Button size="sm" variant="ghost" className="text-destructive shrink-0" disabled={busy} onClick={unassign}>
                {t('task.assignControl.clear')}
              </Button>
            )}
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">{t('task.unassigned')}</span>
        )}

        {editable && !formOpen && (
          <Button size="sm" variant="secondary" onClick={() => setFormOpen(true)}>
            {assignee ? t('task.assignControl.reassign') : t('task.assignControl.assign')}
          </Button>
        )}

        {formOpen && (
          <div className="space-y-2 rounded-md border border-border p-2">
            <UserPicker
              value=""
              onChange={assign}
              scope={peopleScope}
              valueKind="profile"
              placeholder={t('task.field.assigneePlaceholder')}
            />
            {busy && <p className="text-xs text-muted-foreground">{t('common.loading')}</p>}
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setFormOpen(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        )}
    </>
  );

  if (bare) return <div className="w-full space-y-2">{body}</div>;

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {t('task.field.assignee')}
        </p>
        {body}
      </CardContent>
    </Card>
  );
});

export default TaskAssigneeControl;
